import React from 'react';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import * as Yup from 'yup';
import { useFormik, getIn } from 'formik';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import ptBR from 'dayjs/locale/pt-br';

import dayjs from 'dayjs';
import { IEditedRows } from 'components/molecules/StockPositionGrid/types';
import { IModalEntryRemovalDateProps } from './types';
import useStyles from './style';

const FORM_ID = 'DATA_FORM_MODAL';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido';
const MAX_DATE = dayjs().format('YYYY-MM-DD');

const ModalEntryRemovalDate = ({
  children,
  open,
  editedRows,
  onClose,
  onSave,
}: IModalEntryRemovalDateProps) => {
  const classes = useStyles();

  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const validationSchema = Yup.object({
    stock: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().nullable(),
          movementDate: Yup.mixed()
            .when('movementQuantity', movementQuantity => {
              return Number(movementQuantity) !== 0
                ? Yup.mixed().required(DEFAULT_HELPER_TEXT)
                : Yup.mixed().nullable();
            })
            .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
              const test = dayjs(value);
              return (
                dayjs(value).isValid() &&
                (test.isSame(MAX_DATE, 'day') || test.isBefore(MAX_DATE, 'day'))
              );
            }),
        }),
      )
      .min(editedRows.length)
      .required(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      stock: editedRows.map(row => {
        return {
          ...row,
          stockPositionId: row?.stockPositionId,
          movementDate: row?.movementDate,
          movementQuantity: row?.movementQuantity,
        };
      }),
    },
    validationSchema,
    onSubmit: formValues => {
      const edited = formValues.stock.map(item => ({
        ...item,
        movementDate: item.movementDate
          ? dayjs(item.movementDate).format('YYYY-MM-DDTHH:mmZ')
          : null,
        movementQuantity: item.movementQuantity
          ? Number(item.movementQuantity)
          : item.movementQuantity,
        currentQuantity: item.currentQuantity
          ? Number(item.currentQuantity)
          : item.currentQuantity,
        maximumQuantity: item.maximumQuantity
          ? Number(item.maximumQuantity)
          : item.maximumQuantity,
      })) as IEditedRows[];

      setLoadingSaveForm(true);
      onSave(edited)
        .then(() => onClose())
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const PositiveOrNegative = React.useCallback(
    (row, index) => {
      const amount = Number(row?.movementQuantity) || 0;
      const qtdItens = Math.abs(amount);

      const itensOrItem = qtdItens > 1 ? 'itens' : 'item';
      const dosOrDo = qtdItens > 1 ? 'dos' : 'do';

      const firstMessage =
        amount > 0
          ? `Você adicionou ${qtdItens} ${itensOrItem}`
          : `Você removeu ${qtdItens} ${itensOrItem}`;

      const secondMessage =
        amount > 0
          ? `Adicione a data da entrada dos ${qtdItens} ${itensOrItem}:`
          : `Adicione a data da saída ${dosOrDo} ${itensOrItem}:`;

      return amount > 0 ? (
        <>
          <Typography className={classes.formMessage}>
            {firstMessage}
          </Typography>
          <Typography className={classes.formMessage}>
            {secondMessage}
          </Typography>
          <KeyboardDatePicker
            name={`stock.${index}.movementDate`}
            id="movementDate"
            label="Data de Entrada"
            inputVariant="outlined"
            value={formik.values.stock[index]?.movementDate}
            onChange={date => {
              handleDateChange(`stock.[${index}].movementDate`, date);
            }}
            error={
              getIn(formik.touched, `stock.[${index}].movementDate`) &&
              Boolean(getIn(formik.errors, `stock.[${index}].movementDate`))
            }
            helperText={
              (getIn(formik.touched, `stock.[${index}].movementeDate`) &&
                getIn(formik.errors, `stock.[${index}].movementeDate`)) ||
              'Obrigatório'
            }
            format="DD/MM/YYYY"
            className={classes.iconButton}
            cancelLabel="Cancelar"
            clearLabel="Limpar"
            clearable
            maxDate={MAX_DATE}
          />
        </>
      ) : (
        <>
          <Typography className={classes.formMessage}>
            {firstMessage}
          </Typography>
          <Typography className={classes.formMessage}>
            {secondMessage}
          </Typography>
          <KeyboardDatePicker
            name={`stock.${index}.movementDate`}
            id="movementeDate"
            label="Data de Saída"
            inputVariant="outlined"
            value={formik.values.stock[index]?.movementDate}
            onChange={date => {
              handleDateChange(`stock.[${index}].movementDate`, date);
            }}
            error={
              getIn(formik.touched, `stock.[${index}].movementDate`) &&
              Boolean(getIn(formik.errors, `stock.[${index}].movementDate`))
            }
            helperText={
              (getIn(formik.touched, `stock.[${index}].movementeDate`) &&
                getIn(formik.errors, `stock.[${index}].movementeDate`)) ||
              'Obrigatório'
            }
            format="DD/MM/YYYY"
            className={classes.iconButton}
            cancelLabel="Cancelar"
            clearLabel="Limpar"
            clearable
            maxDate={MAX_DATE}
          />
        </>
      );
    },
    [
      classes.formMessage,
      classes.iconButton,
      formik.errors,
      formik.touched,
      formik.values.stock,
      handleDateChange,
    ],
  );

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        {editedRows
          .filter(row => row.movementQuantity !== 0)
          .map((row, index) => {
            return (
              <Grid
                container
                className={classes.bodyContent}
                key={row?.stockPositionId}
              >
                {index > 0 && (
                  <div>
                    <Divider className={classes.divider} />
                  </div>
                )}

                <Grid>
                  <Typography className={classes.formItemMessage}>
                    {`Item Nº ${row?.itemCode}`}
                  </Typography>

                  {PositiveOrNegative(row, index)}
                </Grid>
              </Grid>
            );
          })}
      </form>
    );
  }, [
    PositiveOrNegative,
    classes.bodyContent,
    classes.divider,
    classes.formItemMessage,
    editedRows,
    formik.handleSubmit,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Data de Entrada/Saída"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="492px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};

export default ModalEntryRemovalDate;
